/**
 * Router principal que orquestra todo o wizard de onboarding
 * Renderiza a página apropriada baseado no step atual
 */

import { useEffect, useState } from 'react';
import { useOnboardingWizard } from '../hooks/useOnboardingWizard';
import { loadTemplate, listAvailableTemplates } from '../utils/templateLoader';
import ThemeSelectorPage from './Onboarding/ThemeSelectorPage';
import QuestionPage from './Onboarding/QuestionPage';
import ProcessingPage from './Onboarding/ProcessingPage';
import ShareLinkModal from '../components/Onboarding/ShareLinkModal';
import ErrorModal from '../components/Onboarding/ErrorModal';
import { Box, CircularProgress } from '@mui/material';

export default function OnboardingRouter() {
  const onboarding = useOnboardingWizard();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Limpar e resetar onboarding ao montar (novo usuário)
  useEffect(() => {
    console.log('[OnboardingRouter] Montando OnboardingRouter - novo usuário detectado');
    // Não precisa fazer nada aqui, o hook cuida de restaurar ou inicializar
  }, []);

  // Carregar templates disponíveis
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const loaded = await listAvailableTemplates();
        setTemplates(loaded);
        setLoading(false);
      } catch (error) {
        console.error('[OnboardingRouter] Erro ao carregar templates:', error);
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  // Carregar template selecionado
  useEffect(() => {
    const loadSelectedTemplate = async () => {
      if (onboarding.theme) {
        try {
          const template = await loadTemplate(onboarding.theme);
          setSelectedTemplate(template);
        } catch (error) {
          console.error('[OnboardingRouter] Erro ao carregar template:', error);
          onboarding.setError('Falha ao carregar template');
        }
      }
    };

    loadSelectedTemplate();
  }, [onboarding.theme]);

  // Mostrar loading enquanto carrega templates
  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress sx={{ color: '#ffffff' }} />
      </Box>
    );
  }

  // Renderizar página baseado no step
  const renderPage = () => {
    console.log('[OnboardingRouter] Renderizando step:', onboarding.step, '| theme:', onboarding.theme, '| selectedTemplate:', selectedTemplate);
    
    switch (onboarding.step) {
      case 'theme':
        return (
          <ThemeSelectorPage
            templates={templates}
            onSelectTheme={onboarding.selectTheme}
          />
        );

      case 'question_1':
      case 'question_2':
      case 'question_3':
        // Garantir que template está carregado antes de renderizar pergunta
        if (!selectedTemplate) {
          console.warn('[OnboardingRouter] Template não carregado, aguardando...');
          return (
            <Box
              sx={{
                width: '100%',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              <CircularProgress sx={{ color: '#ffffff' }} />
            </Box>
          );
        }
        
        const questionNumber = parseInt(onboarding.step.split('_')[1]);
        return (
          <QuestionPage
            questionNumber={questionNumber}
            template={selectedTemplate}
            onboarding={onboarding}
          />
        );

      case 'processing':
        return (
          <ProcessingPage
            onboarding={onboarding}
            template={selectedTemplate}
            responses={onboarding.responses}
          />
        );

      case 'completed':
        return (
          <ShareLinkModal
            onboarding={onboarding}
            result={onboarding.result}
            createdIds={onboarding.createdIds}
          />
        );

      case 'error':
        return (
          <ErrorModal
            error={onboarding.error}
            onRetry={onboarding.recoverFromError}
            onCancel={onboarding.cancelOnboarding}
          />
        );

      default:
        return (
          <ThemeSelectorPage
            templates={templates}
            onSelectTheme={onboarding.selectTheme}
          />
        );
    }
  };

  return (
    <div style={{ width: '100vw', height: '100vh', margin: 0, padding: 0, boxSizing: 'border-box' }}>
      {renderPage()}
    </div>
  );
}
